import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DomainSelector from '../components/shared/DomainSelector';
import StatusMessage from '../components/CategoryDescription/StatusMessage';
import { ErrorBoundary } from '../components/shared/ErrorBoundary';
import FeatureFlagOverlay from '../components/shared/FeatureFlagOverlay';
import { FeatureFlag } from '../utils/featureFlags';
import { useData } from '../contexts/DataContext';
import { useNotification } from '../contexts/NotificationContext';
import { Domain } from '../types/data';
import ModeSelector from '../components/ArticleGeneration/ModeSelector';
import TagInput from '../components/ArticleGeneration/TagInput';
import ContextCard from '../components/ArticleGeneration/ContextCard';
import TextAmountSlider from '../components/ProductDescription/TextAmountSlider';
import { CustomerGroupSelector } from '../components/shared/CustomerGroupSelector';
import RichTextEditor from '../components/shared/RichTextEditor';
import { ArticleGenerationRow, ArticleMode, ArticlePart } from '../types/articleGeneration';
import { articleGenerationService } from '../services/articleGenerationService';
import { useArticleGeneration } from '../components/ArticleGeneration/useArticleGeneration';
import ArticleWorkspace from '../components/ArticleGeneration/ArticleWorkspace';
import { estimateMinutes, isThinEvidence } from '../components/ArticleGeneration/generationHints';
import {
  DEFAULT_SECTION_LENGTH,
  levelFromSectionLength,
  SECTION_LENGTH_LEVELS,
  SECTION_LENGTH_WORDS,
  sectionLengthFromLevel,
} from '../components/ArticleGeneration/articleLength';
import { SectionLength } from '../types/articleGeneration';
import PageTabs from '../components/shared/PageTabs';
import HistoryPanel from '../components/ArticleHistory/HistoryPanel';
import LearningsPanel from '../components/ArticleLearnings/LearningsPanel';

const ArticleGeneration: React.FC = () => {
  const {
    domains,
    targetCompany,
    fetchRelatedDomainData,
    fetchDomains,
    customerTargetGroups,
    companies,
  } = useData();
  const [activeDomain, setActiveDomain] = useState<Domain | null>(null);
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const [mode, setMode] = useState<ArticleMode>('full_article');
  const [headingCount, setHeadingCount] = useState(5);
  const [sectionLength, setSectionLength] = useState<SectionLength>(DEFAULT_SECTION_LENGTH);
  const [sourceNotes, setSourceNotes] = useState('');
  const [quotes, setQuotes] = useState('');
  const [sourceData, setSourceData] = useState('');
  const [primaryKeyword, setPrimaryKeyword] = useState('');
  const [secondaryKeywords, setSecondaryKeywords] = useState<string[]>([]);
  const [selectedCustomerGroup, setSelectedCustomerGroup] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  // A run opened from history may belong to another domain than the one the
  // domain-scoped runs list holds; load it directly so the workspace can show it.
  const [externalRun, setExternalRun] = useState<ArticleGenerationRow | null>(null);

  const {
    runs,
    progress,
    activeMode,
    elapsedMs,
    isGenerating,
    generationId,
    start,
    cancel,
    patchRunLocally,
  } = useArticleGeneration(targetCompany, activeDomain?.id ?? null, (message) =>
    showNotification(message, 'error')
  );

  const [searchParams, setSearchParams] = useSearchParams();
  // Tabs: ?tab=history → history; ?tab=learnings → learnings; ?tab=workspace or
  // ?run=<id> → workspace; else generate.
  const tabParam = searchParams.get('tab');
  const runParam = searchParams.get('run');
  // The LLM log lives in the article's details; an old ?tab=log link opens it there.
  const activeTab = tabParam === 'history'
    ? 'history'
    : tabParam === 'learnings'
      ? 'learnings'
      : tabParam === 'workspace' || tabParam === 'log' || runParam
        ? 'workspace'
        : 'generate';

  // A ?run deep link (from Generation history) focuses the workspace on that run.
  useEffect(() => {
    if (!runParam) return;
    setActiveRunId(runParam);
    if (!runs.some((r) => r.id === runParam)) {
      articleGenerationService
        .getGeneration(runParam)
        .then((row) => row && setExternalRun(row))
        .catch(() => undefined);
    }
  }, [runParam, runs]);

  // As soon as a fresh generation gets its id, focus the workspace on it.
  useEffect(() => {
    if (generationId) setActiveRunId(generationId);
  }, [generationId]);

  const goToTab = (id: string) =>
    setSearchParams(id === 'generate' ? {} : { tab: id });

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    if (targetCompany && domains.length > 0) {
      const foundDomain = domains.find((domain: Domain) => domain.company_id === targetCompany);
      if (foundDomain) {
        setActiveDomain(foundDomain);
        fetchRelatedDomainData(foundDomain.id);
      } else {
        setActiveDomain(null);
      }
    }
  }, [domains, targetCompany]);

  const activeCompany = companies.find((c) => c.id === targetCompany);
  const hasContent = [sourceNotes, quotes, sourceData].some((s) => s.trim() !== '');
  const isValid = primaryKeyword.trim() !== '' && selectedCustomerGroup !== '' && hasContent;
  const activeRun =
    runs.find((r) => r.id === activeRunId) ??
    (externalRun?.id === activeRunId ? externalRun : null);

  // The pipeline runs server-side, so poll a run that is still generating (and
  // that this client isn't live-streaming) to reflect its progress and final
  // result even after a reload or a dropped stream.
  const activeRunStatus = activeRun?.status;
  useEffect(() => {
    if (!activeRunId || isGenerating || activeRunStatus !== 'generating') return;
    let stop = false;
    const poll = async () => {
      const row = await articleGenerationService.getGeneration(activeRunId).catch(() => null);
      if (stop || !row) return;
      patchRunLocally(row.id, row);
      setExternalRun((prev) => (prev && prev.id === row.id ? row : prev));
    };
    const iv = setInterval(poll, 3500);
    poll();
    return () => {
      stop = true;
      clearInterval(iv);
    };
  }, [activeRunId, activeRunStatus, isGenerating]);

  const headingDescriptions = Object.fromEntries(
    Array.from({ length: 10 }, (_, i) => [i + 1, t('article.heading_count_estimate', { count: i + 1 })])
  );
  const sectionLengthDescriptions = Object.fromEntries(
    SECTION_LENGTH_LEVELS.map((length, i) => {
      const [min, max] = SECTION_LENGTH_WORDS[length];
      return [i + 1, `${t(`article.section_length_${length}`)}: ${t('article.section_length_estimate', { min, max })}`];
    })
  );

  const handleGenerate = async () => {
    if (!isValid || !activeDomain) {
      showNotification(t('article.validation_error'), 'error');
      return;
    }
    // Each generation is its own article; move to the workspace to watch it build.
    goToTab('workspace');
    await start(
      {
        action: 'generate',
        mode,
        headingCount,
        sourceNotes,
        quotes,
        sourceData,
        primaryKeyword: primaryKeyword.trim(),
        secondaryKeywords,
        language: activeDomain.language || 'English',
        toneOfVoice: activeDomain.tone_of_voice || 'Professional',
        customerGroupDescription:
          customerTargetGroups.find((group) => group.name === selectedCustomerGroup)
            ?.description || '',
        customPrompt,
        sectionLength,
        companyId: targetCompany || '',
        domainId: activeDomain.id,
        domainName: activeDomain.domain || '',
      },
      mode,
      headingCount
    );
  };

  const handleContinue = async (generationId: string, runMode: ArticleMode, count: number) => {
    await start({ action: 'resume', generationId, companyId: targetCompany || '' }, runMode, count);
  };

  const handleRegeneratePart = async (
    run: ArticleGenerationRow,
    part: ArticlePart,
    note: string
  ) => {
    await start(
      { action: 'regenerate_part', generationId: run.id, companyId: targetCompany || '', part, note },
      run.mode,
      run.inputs.headingCount
    );
  };

  // A finished plan becomes a new full article that keeps its headings. The
  // backend copies the inputs, structure and briefs from the plan run.
  const handleWriteFromPlan = async (plan: ArticleGenerationRow) => {
    if (!activeDomain || !plan.structure) return;
    goToTab('workspace');
    await start(
      {
        action: 'generate',
        mode: 'full_article',
        planFrom: plan.id,
        headingCount: plan.structure.headings.length,
        sourceNotes: plan.inputs.sourceNotes,
        quotes: plan.inputs.quotes,
        sourceData: plan.inputs.sourceData,
        primaryKeyword: plan.inputs.primaryKeyword,
        secondaryKeywords: plan.inputs.secondaryKeywords,
        language: plan.inputs.language,
        toneOfVoice: plan.inputs.toneOfVoice,
        customerGroupDescription: plan.inputs.customerGroupDescription,
        customPrompt: plan.inputs.customPrompt,
        sectionLength: plan.inputs.sectionLength,
        companyId: targetCompany || '',
        domainId: plan.domain_id,
        domainName: activeDomain.domain || '',
      },
      'full_article',
      plan.structure.headings.length
    );
  };

  const handleRecritique = async (generationId: string, runMode: ArticleMode, count: number) => {
    await start({ action: 'recritique', generationId, companyId: targetCompany || '' }, runMode, count);
  };

  // Stop a run that continues server-side: mark it cancelled; the pipeline
  // honours that at its next checkpoint.
  const handleStop = async (run: ArticleGenerationRow) => {
    try {
      await articleGenerationService.cancelGeneration(run.id);
      patchRunLocally(run.id, { status: 'cancelled' });
      setExternalRun((prev) => (prev && prev.id === run.id ? { ...prev, status: 'cancelled' } : prev));
    } catch (error) {
      showNotification(error instanceof Error ? error.message : t('article.generation_error'), 'error');
    }
  };

  if (!targetCompany || !activeDomain) {
    return (
      <StatusMessage targetCompany={targetCompany} activeDomain={activeDomain?.domain || null} />
    );
  }

  return (
    <FeatureFlagOverlay feature={FeatureFlag.ARTICLE_GENERATION}>
      <div className="p-8">
        <ErrorBoundary>
          <h1 className="text-3xl font-bold mb-2">{t('article.title')}</h1>
          <div className="mt-4">
            <PageTabs
              tabs={[
                { id: 'generate', label: t('article.tab_generate') },
                { id: 'workspace', label: t('article.tab_workspace') },
                { id: 'history', label: t('sidebar.articleHistory') },
                { id: 'learnings', label: t('article.tab_learnings') },
              ]}
              active={activeTab}
              onChange={goToTab}
            />
          </div>

          {activeTab === 'history' ? (
            <HistoryPanel />
          ) : activeTab === 'learnings' ? (
            <LearningsPanel companyId={targetCompany} domainId={activeDomain.id} />
          ) : activeTab === 'workspace' ? (
            <ArticleWorkspace
              run={activeRun}
              runs={runs}
              isGenerating={isGenerating}
              progress={progress}
              activeMode={activeMode}
              elapsedMs={elapsedMs}
              onCancel={cancel}
              onStop={handleStop}
              onContinue={(run) => handleContinue(run.id, run.mode, run.inputs.headingCount)}
              onRecritique={(run) => handleRecritique(run.id, run.mode, run.inputs.headingCount)}
              onRegeneratePart={(part, note) =>
                activeRun && handleRegeneratePart(activeRun, part, note)
              }
              onNoteSaved={(patch) => activeRun && patchRunLocally(activeRun.id, patch)}
              onPatched={(patch) => activeRun && patchRunLocally(activeRun.id, patch)}
              onWriteArticle={handleWriteFromPlan}
              onOpenRun={(id) => setSearchParams({ run: id })}
              historyView={!!runParam}
              initialLogOpen={tabParam === 'log'}
            />
          ) : (
            <>
          <div className="mb-6">
            <DomainSelector activeDomain={activeDomain} setActiveDomain={setActiveDomain} />
          </div>

          <div className="mb-6">
            <CustomerGroupSelector
              customerGroups={
                customerTargetGroups?.map((group) => ({
                  name: group.name,
                  description: group.description || '',
                })) || []
              }
              selectedGroup={selectedCustomerGroup}
              onSelect={setSelectedCustomerGroup}
            />
          </div>

          <div className="mb-6">
            <ContextCard
              company={activeCompany?.name || ''}
              domain={activeDomain.domain}
              language={activeDomain.language || 'English'}
              customerGroup={selectedCustomerGroup}
            />
          </div>

          <div className="mb-6">
            <ModeSelector mode={mode} onChange={setMode} />
          </div>

          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <TextAmountSlider
              label={t('article.heading_count')}
              value={headingCount}
              onChange={setHeadingCount}
              descriptions={headingDescriptions}
              min={1}
              max={10}
              minLabel="1"
              maxLabel="10"
              compact
              testId="heading-count-slider"
            />
            <div>
              <TextAmountSlider
                label={t('article.section_length')}
                value={levelFromSectionLength(sectionLength)}
                onChange={(level) => setSectionLength(sectionLengthFromLevel(level))}
                descriptions={sectionLengthDescriptions}
                min={1}
                max={3}
                minLabel={t('article.section_length_short')}
                maxLabel={t('article.section_length_long')}
                compact
                testId="section-length-slider"
              />
              <p className="mt-1 text-xs text-gray-500">{t('article.section_length_hint')}</p>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">{t('article.source_notes')}</label>
            <textarea
              value={sourceNotes}
              onChange={(e) => setSourceNotes(e.target.value)}
              placeholder={t('article.source_notes_placeholder')}
              rows={10}
              className="w-full p-3 border rounded-md"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">{t('article.quotes')}</label>
            <textarea
              value={quotes}
              onChange={(e) => setQuotes(e.target.value)}
              placeholder={t('article.quotes_placeholder')}
              rows={6}
              className="w-full p-3 border rounded-md"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">{t('article.source_data')}</label>
            <textarea
              value={sourceData}
              onChange={(e) => setSourceData(e.target.value)}
              placeholder={t('article.source_data_placeholder')}
              rows={6}
              className="w-full p-3 border rounded-md"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">{t('article.primary_keyword')}</label>
            <input
              value={primaryKeyword}
              onChange={(e) => setPrimaryKeyword(e.target.value)}
              className="w-full p-3 border rounded-md"
            />
          </div>

          <div className="mb-6">
            <TagInput
              label={t('article.secondary_keywords')}
              value={secondaryKeywords}
              onChange={setSecondaryKeywords}
              placeholder={t('article.secondary_keywords_placeholder')}
            />
          </div>

          <div className="mb-6">
            <ErrorBoundary>
              <RichTextEditor
                label={t('common.custom_prompt')}
                value={customPrompt}
                onChange={setCustomPrompt}
                simpleText={true}
              />
            </ErrorBoundary>
          </div>

          {(() => {
            const est = estimateMinutes(mode, headingCount);
            const thin = isThinEvidence({ sourceNotes, quotes, sourceData });
            return (
              <div className="mb-4 space-y-2 text-sm">
                {thin && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
                    {t('article.evidence_thin_warning')}
                  </div>
                )}
                <div className="text-gray-500">
                  {t('article.duration_estimate', { min: est.min, max: est.max })}
                </div>
              </div>
            );
          })()}
          <button
            disabled={!isValid || isGenerating}
            onClick={handleGenerate}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:bg-gray-400"
          >
            {t(`article.generate_${mode}`)}
          </button>
            </>
          )}
        </ErrorBoundary>
      </div>
    </FeatureFlagOverlay>
  );
};

export default ArticleGeneration;
